/**
 * DocumentList Component
 * Displays a list of documents for a collection
 */

import { parse, format, isValid } from "date-fns";
import { useState } from "react";

import type { CollectionDocument } from "../../types/admin";
import { DocumentEditModal } from "./DocumentEditModal";

interface DocumentListProps {
  documents: CollectionDocument[];
  isLoading: boolean;
  error: Error | null;
}

const MIME_TYPE_ICONS: Record<string, string> = {
  "application/pdf": "📄",
  "application/vnd.google-apps.document": "📝",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "📝",
  "text/plain": "📃",
  "text/html": "🌐",
};

const DEFAULT_MIME_ICON = "📋";

function getMimeTypeIcon(mimeType: string | null): string {
  if (!mimeType) return DEFAULT_MIME_ICON;
  return MIME_TYPE_ICONS[mimeType] ?? DEFAULT_MIME_ICON;
}

function StatusBadge({
  conversionStatus,
  extractionStatus,
}: {
  conversionStatus: CollectionDocument["conversionStatus"];
  extractionStatus: CollectionDocument["extractionStatus"];
}) {
  const hasError = conversionStatus === "error" || extractionStatus === "error";
  const isComplete = conversionStatus === "completed" && extractionStatus === "completed";
  let badge: { icon: string; label: string; className: string };

  if (hasError) {
    badge = { icon: "✗", label: "Fout", className: "bg-error-container text-on-error-container" };
  } else if (isComplete) {
    badge = {
      icon: "✓",
      label: "Verwerkt",
      className: "bg-primary-container text-on-primary-container",
    };
  } else {
    badge = {
      icon: "⏳",
      label: "In behandeling",
      className: "bg-tertiary-container text-on-tertiary-container",
    };
  }

  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}
      title={badge.label}
    >
      {badge.icon}
    </span>
  );
}

function LockBadge({ locked }: { locked: boolean }) {
  if (locked) {
    return (
      <span
        className="px-2 py-0.5 rounded-sm text-xs font-medium bg-secondary-container text-on-secondary-container"
        title="Metadata vergrendeld"
      >
        🔒
      </span>
    );
  }
  return null;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "-";

  // Handle YYYYMMDD format from metadata parser
  if (/^\d{8}$/.test(dateString)) {
    const date = parse(dateString, "yyyyMMdd", new Date());
    if (isValid(date)) {
      return format(date, "yyyy-MM-dd");
    }
  }

  // Handle YYYYMMD format (7 digits) from metadata parser
  if (/^\d{7}$/.test(dateString)) {
    const date = parse(dateString, "yyyyMMd", new Date());
    if (isValid(date)) {
      return format(date, "yyyy-MM-dd");
    }
  }

  // Try parsing as ISO date string
  const date = new Date(dateString);
  if (isValid(date)) {
    return format(date, "yyyy-MM-dd");
  }

  return "-";
}

function DocumentRow({
  document,
  onEdit,
}: {
  document: CollectionDocument;
  onEdit: (document: CollectionDocument) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-surface-container-low rounded-sm border-b border-outline-variant/50 last:border-0">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="text-lg" title={document.mimeType || undefined}>
          {getMimeTypeIcon(document.mimeType)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-on-surface truncate" title={document.name}>
              {document.title}
            </span>
            {document.category && (
              <span className="px-1.5 py-0.5 rounded-sm text-xs bg-primary-container text-on-primary-container">
                {document.category}
              </span>
            )}
          </div>
          <div className="text-xs text-on-surface-variant mt-0.5 flex items-center gap-2">
            {document.documentDate && (
              <span className="text-on-surface-variant font-medium">
                📅 {formatDate(document.documentDate)}
              </span>
            )}
            {!document.documentDate && <span className="text-outline italic">geen datum</span>}
            {document.governanceBody && <span>{document.governanceBody}</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 ml-3">
        <StatusBadge
          conversionStatus={document.conversionStatus}
          extractionStatus={document.extractionStatus}
        />
        <LockBadge locked={document.metadataLocked} />
        {(document.conversionError ?? document.extractionError) && (
          <span
            className="px-2 py-0.5 rounded-sm text-xs font-medium bg-error-container text-on-error-container"
            title={`${document.conversionError ?? ""}${
              document.conversionError && document.extractionError ? " — " : ""
            }${document.extractionError ?? ""}`}
          >
            ⚠️ fout
          </span>
        )}
        <button
          onClick={() => onEdit(document)}
          className="p-1.5 text-on-surface-variant/50 hover:text-primary hover:bg-primary-container/30 rounded-sm transition-colors"
          title="Bewerk metadata"
        >
          ✏️
        </button>
        {document.sourceUrl && (
          <a
            href={document.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 text-on-surface-variant/50 hover:text-primary hover:bg-primary-container/30 rounded-sm transition-colors"
            title="Open origineel document"
          >
            🔗
          </a>
        )}
      </div>
    </div>
  );
}

export function DocumentList({ documents, isLoading, error }: DocumentListProps) {
  const [editingDocument, setEditingDocument] = useState<CollectionDocument | null>(null);

  if (isLoading) {
    return (
      <div className="py-6 text-center text-on-surface-variant">
        <div className="animate-pulse">📂 Documenten laden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center text-error bg-error-container/50 rounded-sm">
        <p className="font-medium">Fout bij laden documenten</p>
        <p className="text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="py-6 text-center text-on-surface-variant">
        <p>Geen documenten gevonden</p>
        <p className="text-sm mt-1">Gebruik Discover/Download om documenten toe te voegen</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-surface-dim rounded-md">
        <div className="max-h-80 overflow-y-auto">
          {documents.map((document) => (
            <DocumentRow
              key={document.documentId}
              document={document}
              onEdit={setEditingDocument}
            />
          ))}
        </div>
        <div className="px-3 py-2 text-xs text-on-surface-variant border-t border-outline-variant">
          {documents.length} document{documents.length !== 1 ? "en" : ""}
          {" • "}
          {documents.filter((f) => f.extractionStatus === "completed").length} metadata geëxtraheerd
        </div>
      </div>

      {editingDocument && (
        <DocumentEditModal
          document={editingDocument}
          isOpen={true}
          onClose={() => setEditingDocument(null)}
        />
      )}
    </>
  );
}
